import { Alert, Box, Button, Card, CardHeader, Chip, Divider, IconButton, MenuItem, Select, Stack, Table, TableBody, TextField as MuiTextField, Tooltip, Typography, keyframes, css } from "@mui/material"
import { ChangeEvent, FC, useCallback, useState, createContext, useContext } from "react"
import Confirm from "../widgets/Confirm"
import WarningIcon from '@mui/icons-material/Warning';
import { useNetworkConfigurationContext } from "~/pages/deployment/useNetworkConfigurationContext"
import { TRow, InfoPiece } from './DeploymentInfo'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';
import NewVPNGateway from "./NewVPNGateway";
import { useGetMainList } from "ra-core";
import { RecPatch } from "~/dataProvider/tools";
import { OptionResponseDto } from "~/gen/api/user";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import { useUserTimeFormat } from "~/utils/userTimeFormat";
import { NetworkGatewayStatus } from "~/gen/api/deploy";
import { CLOUD_MANAGEMENT } from "~/constants";
import { useHasPermit } from "~/authProvider/useHasPermit";


const pulseKeyframe = keyframes`
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.4;
  }

  100% {
    opacity: 1;
  }
`;

const pulseCss = css`
  animation: ${pulseKeyframe} 1.5s ease-in-out 0.5s infinite;
`


const resource = 'settings/deployment/network-configuration'

const NetworkGatewayInfo: FC = () => {
  const hasWritePermit = useHasPermit([CLOUD_MANAGEMENT])
  const { shouldSuspendUsage } = useContext(NetworkConfigureContext)
  
  const { deleteGateway, gatewayDeleting, vpnGatewayRec: gatewayRec, changeGatewayTier } = useNetworkConfigurationContext()

  const [open, setOpen] = useState(false)
  const [nameConfirm, setNameConfirm] = useState('')

  const nameConfirmed = !!(gatewayRec && (gatewayRec.name?.toLowerCase() === nameConfirm?.toLowerCase()))

  const handleNameInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setNameConfirm(e.target.value)
  }, [])

  const handleDeleteDialogClose = () => {
    setOpen(false)
    setNameConfirm('')
  }

  const handleDelete = () => {
    deleteGateway({
      onSuccess: () => {
        handleDeleteDialogClose()
      }
    })
  }


  const [openCreate, setOpenCreate] = useState(false)
  const [mode, setMode] = useState<'info'|'update'>('info')
  const [currTier, setCurrTier] = useState(gatewayRec?.performanceTier || '')

  const { data: skuData } = useGetMainList<OptionResponseDto & RecPatch>('network-connection-sku', pages(), sorts(), {})
  const performanceTiers = Object.values(skuData || {})

  const handleCancel = () => {
    setMode('info')
    setCurrTier(gatewayRec?.performanceTier || '')
  }

  const performChangeTier = () => {
    changeGatewayTier(currTier, {
      onSuccess: () => { 
        setMode('info')
      }
    })
  }

  const format = useUserTimeFormat()
  
  return (
    <Box>
      <Card>
        <CardHeader 
          title="VPN Gateway" 
          action={
            gatewayRec && hasWritePermit &&
              <Stack direction="row" spacing={2}>
                {mode === 'info' ?
                  <>
                    {/* <Tooltip title="Change">
                      <IconButton 
                        sx={{ 
                          color: 'text.primary' as any, 
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        onClick={() => setMode('update')}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip> */}
                    <Tooltip title="Delete">
                      <IconButton 
                        sx={{ 
                          color: 'text.primary' as any, 
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        onClick={() => setOpen(true)}
                      >
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </> :
                  <>
                    <Button
                      size="small"
                      color="primary"
                      variant="outlined"
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      color="primary"
                      variant="contained"
                      onClick={performChangeTier}
                    >
                      Save
                    </Button>
                  </>
                }
              </Stack>
          }
        />
        <Divider />
        {gatewayRec ?
          <>
            <Table>
              <TableBody>
                <TRow>
                  <InfoPiece label="Name">
                    {gatewayRec?.name}
                  </InfoPiece>
                  <InfoPiece label="Status">
                    <Chip
                      sx={{ 
                        my: -1, 
                        ...((
                          gatewayRec.status === NetworkGatewayStatus.Deleting ||
                          gatewayRec.status === NetworkGatewayStatus.InProgress ||
                          gatewayRec.status === NetworkGatewayStatus.Updating) &&
                          pulseCss)
                      }}
                      label={gatewayRec?.statusText || ''}
                      color={
                        (gatewayRec.status === NetworkGatewayStatus.Success
                        ? 'success'
                        : (gatewayRec.status === NetworkGatewayStatus.Updating || gatewayRec.status === NetworkGatewayStatus.InProgress) 
                          ? 'default'
                        : gatewayRec.status === NetworkGatewayStatus.Deleting ? 'warning'
                        : gatewayRec.status === NetworkGatewayStatus.Failed ? 'error'
                        : 'default') as any
                      }
                    />
                  </InfoPiece>
                </TRow>
                <TRow>
                  <InfoPiece label="Public IP Address">
                    {gatewayRec?.onDataPublicIp}
                  </InfoPiece>
                  <InfoPiece label="Performance Tier">
                    {mode === 'info' ?
                      (gatewayRec?.performanceTier && 
                        performanceTiers.find(p => p.value === gatewayRec.performanceTier)?.displayName) :
                      <Select
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={currTier}
                        onChange={(e) => setCurrTier(e.target.value)}
                      >
                        {performanceTiers.map(t => (
                          <MenuItem key={t.value} value={t.value || ''}>{t.displayName}</MenuItem>
                        ))}
                      </Select>
                    }
                  </InfoPiece>
                </TRow>
                <TRow>
                  <InfoPiece label="Creation Time">
                    {format(gatewayRec?.createdTime || 0, 'Pp')}
                  </InfoPiece>
                  <InfoPiece label="" />
                </TRow>
              </TableBody>
            </Table>
          </> :
          (hasWritePermit &&
            <Box
              sx={{ m: 2 }}
            >
              <Button
                disabled={shouldSuspendUsage}
                fullWidth
                size="large"
                color="primary"
                variant="text"
                startIcon={<AddIcon />}
                onClick={() => !shouldSuspendUsage && setOpenCreate(true)}
              >
                Deploy VPN Gateway
              </Button>
              <NewVPNGateway open={!shouldSuspendUsage && openCreate} setOpen={setOpenCreate} />
            </Box>)
        }
      </Card>
      {mode === 'update' &&
        <Alert severity="warning" sx={{ mt: 2 }}>
          Change Performance Tier requires some re-arrangement for the network infrastructure, 
          and it will affect all existing Network Connections.
          You may experience a few minutes of downtime for the database connections. Please do
          aware of the impact and plan ahead of time.
        </Alert>
      }
      {hasWritePermit &&
        <Confirm
          isOpen={open}
          loading={gatewayDeleting}
          valid={nameConfirmed}
          title={"Delete VPN Gateway"}
          onConfirm={handleDelete}
          onClose={handleDeleteDialogClose}
          confirm="Confirm"
          cancel="Cancel"
          content={(
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WarningIcon fontSize="large" sx={{ color: 'warning.main' }}/>
                <Typography
                  component="span"
                  variant="body2" 
                  sx={{ ml: 1 }}
                >
                  Warning! Deleting VPN Gateway "{gatewayRec?.name}" is irreversible. The action you are about to take cannot be undone.
                </Typography>
              </Box>
              <MuiTextField
                id="confirm-gateway-name"
                autoFocus
                fullWidth
                error={!nameConfirmed}
                helperText={!nameConfirmed && "Enter the name of your VPN Gateway to confirm delete."}
                size="medium"
                label="Type VPN Gateway Name"
                sx={{ mb: 2, mt: 4, }}
                onChange={handleNameInput}
                value={nameConfirm}
              />
            </Box>
          )}
        />}
    </Box>
  )
}

export default NetworkGatewayInfo;



interface NetworkConfigureContextValue {
  shouldSuspendUsage: boolean,
}

export const NetworkConfigureContext = createContext<NetworkConfigureContextValue>({
  shouldSuspendUsage: true,
})

